(() => {
  if (window.humblerProductReelsReady) return;
  window.humblerProductReelsReady = true;
  const init = (root) => {
      if (!root || root.dataset.productReelsReady === 'true') return;
      root.dataset.productReelsReady = 'true';

      const track = root.querySelector('[data-product-reels-track]');
      const dialog = root.querySelector('[data-product-reels-dialog]');
      const viewer = root.querySelector('[data-product-reels-viewer]');
      const cards = Array.from(root.querySelectorAll('[data-reel-open]'));
      const modalSlides = Array.from(root.querySelectorAll('[data-reel-slide]'));
      const modalVideos = modalSlides.map((slide) => slide.querySelector('video'));
      const thumbnails = Array.from(root.querySelectorAll('[data-reel-thumb]'));
      const shopDock = root.querySelector('[data-product-reels-shop-dock]');
      const shopBody = root.querySelector('[data-product-reels-shop-body]');
      const inlineAutoplay = root.dataset.inlineAutoplay === 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let activeIndex = 0;
      let modalMuted = false;
      let inlineObserver;
      let modalObserver;
      let closeTimer;
      const eventController = new AbortController();
      const eventOptions = { signal: eventController.signal };

      const loadVideo = (video) => {
        if (video.dataset.reelLoaded === 'true') return;
        const sources = [video, ...video.querySelectorAll('[data-reel-src]')];
        sources.forEach((source) => {
          if (!source.dataset.reelSrc) return;
          source.src = source.dataset.reelSrc;
          source.removeAttribute('data-reel-src');
        });
        if (video.dataset.reelPoster) {
          video.poster = video.dataset.reelPoster;
          video.removeAttribute('data-reel-poster');
        }
        video.dataset.reelLoaded = 'true';
        video.preload = 'metadata';
        video.load();
      };

      const playVideo = (video, allowMutedFallback = false) => {
        if (!video || document.hidden) return Promise.resolve();
        loadVideo(video);
        const promise = video.play();
        if (!promise || !promise.catch) return Promise.resolve();
        return promise.catch(() => {
          if (!allowMutedFallback || video.muted) return;
          video.muted = true;
          return video.play().catch(() => {});
        });
      };

      const pauseVideo = (video) => {
        if (!video) return;
        video.pause();
      };

      const isInlineCardVisible = (card) => {
        const rect = card.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
        return visibleWidth > rect.width * 0.5 && visibleHeight > rect.height * 0.28;
      };

      const resumeInlineVideos = () => {
        if (!inlineAutoplay || reducedMotion || document.hidden || (dialog && !dialog.hidden)) return;
        cards.forEach((card) => {
          const video = card.querySelector('video');
          if (!video) return;
          video.muted = true;
          if (isInlineCardVisible(card)) {
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      };

      root.querySelectorAll('[data-reel-scroll]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!track) return;
          const direction = Number(button.dataset.reelScroll || 1);
          track.scrollBy({
            left: direction * track.clientWidth * 0.82,
            behavior: reducedMotion ? 'auto' : 'smooth'
          });
        });
      });

      if (track && inlineAutoplay && !reducedMotion && 'IntersectionObserver' in window) {
        inlineObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            if (!video) return;
            video.muted = true;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.28 && !document.hidden && (!dialog || dialog.hidden)) {
              playVideo(video);
            } else {
              pauseVideo(video);
            }
          });
        }, { root: null, threshold: 0.28 });

        cards.forEach((card) => inlineObserver.observe(card));
      }

      const syncActiveUi = () => {
        thumbnails.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === activeIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', String(isActive));
        });

        if (!shopDock || !shopBody) return;
        shopBody.replaceChildren();
        const productList = modalSlides[activeIndex] && modalSlides[activeIndex].querySelector('[data-reel-products]');
        if (!productList || !productList.children.length) {
          shopDock.hidden = true;
          shopDock.closest('.product-reels__player-layout').classList.remove('has-products');
          return;
        }

        const productClone = productList.cloneNode(true);
        productClone.classList.add('product-reels__shop-products');
        shopBody.appendChild(productClone);
        shopDock.hidden = false;
        shopDock.closest('.product-reels__player-layout').classList.add('has-products');
      };

      const setActiveSlide = (index) => {
        if (!modalVideos.length || !dialog || dialog.hidden || !dialog.classList.contains('is-open')) return;
        activeIndex = Math.max(0, Math.min(index, modalVideos.length - 1));
        modalVideos.forEach((video, videoIndex) => {
          if (!video) return;
          video.muted = modalMuted;
          if (videoIndex === activeIndex) {
            playVideo(video, true);
          } else {
            pauseVideo(video);
          }
        });
        syncActiveUi();
      };

      const openDialog = (index) => {
        if (!dialog || !viewer) return;
        window.clearTimeout(closeTimer);
        cards.forEach((card) => pauseVideo(card.querySelector('video')));
        dialog.hidden = false;
        dialog.classList.add('is-open');
        document.body.classList.add('product-reels-modal-open');
        const slide = modalSlides[index];
        if (slide) viewer.scrollTo({ top: slide.offsetTop, behavior: 'auto' });
        setActiveSlide(index);
        window.requestAnimationFrame(() => {
          if (slide) viewer.scrollTo({ top: slide.offsetTop, behavior: 'auto' });
          setActiveSlide(index);
        });
      };

      const closeDialog = () => {
        if (!dialog) return;
        dialog.classList.remove('is-open');
        modalVideos.forEach(pauseVideo);
        document.body.classList.remove('product-reels-modal-open');
        closeTimer = window.setTimeout(() => {
          dialog.hidden = true;
          window.requestAnimationFrame(resumeInlineVideos);
        }, reducedMotion ? 0 : 180);
      };

      cards.forEach((card) => {
        card.addEventListener('click', () => openDialog(Number(card.dataset.reelOpen || 0)));
      });

      thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!viewer) return;
          const nextIndex = Number(thumb.dataset.reelThumb || 0);
          const slide = modalSlides[nextIndex];
          if (slide) {
            viewer.scrollTo({
              top: slide.offsetTop,
              behavior: reducedMotion ? 'auto' : 'smooth'
            });
          }
          setActiveSlide(nextIndex);
        });
      });

      root.querySelectorAll('[data-reel-close]').forEach((button) => {
        button.addEventListener('click', closeDialog);
      });

      if (dialog) {
        dialog.addEventListener('click', (event) => {
          if (!event.target.closest('.product-reels__player-layout') && !event.target.closest('[data-reel-close]')) {
            closeDialog();
          }
        });
      }

      document.addEventListener('keydown', (event) => {
        if (!dialog || dialog.hidden) return;
        if (event.key === 'Escape') closeDialog();
      }, eventOptions);

      const muteButton = root.querySelector('[data-reel-mute]');
      if (muteButton) {
        muteButton.addEventListener('click', () => {
          modalMuted = !modalMuted;
          muteButton.setAttribute('aria-pressed', String(!modalMuted));
          modalVideos.forEach((video) => {
            if (video) video.muted = modalMuted;
          });
          playVideo(modalVideos[activeIndex], true);
        });
      }

      const shareButton = root.querySelector('[data-reel-share]');
      if (shareButton) {
        shareButton.addEventListener('click', async () => {
          const shareUrl = window.location.href.split('#')[0];
          try {
            if (navigator.share) {
              await navigator.share({ title: document.title, url: shareUrl });
            } else if (navigator.clipboard) {
              await navigator.clipboard.writeText(shareUrl);
            }
          } catch (error) {
            return;
          }
        });
      }

      modalVideos.forEach((video, index) => {
        if (!video) return;
        video.addEventListener('click', () => {
          if (video.paused) {
            setActiveSlide(index);
          } else {
            pauseVideo(video);
          }
        });
      });

      if (viewer && 'IntersectionObserver' in window) {
        modalObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.68 && !dialog.hidden && dialog.classList.contains('is-open')) {
              setActiveSlide(Number(entry.target.dataset.reelSlide || 0));
            }
          });
        }, { root: viewer, threshold: 0.68 });

        modalSlides.forEach((slide) => modalObserver.observe(slide));
      }

      const pauseAll = () => {
        cards.forEach((card) => pauseVideo(card.querySelector('video')));
        modalVideos.forEach(pauseVideo);
      };
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) pauseAll();
        else if (dialog && !dialog.hidden) setActiveSlide(activeIndex);
        else resumeInlineVideos();
      }, eventOptions);
      window.addEventListener('pagehide', pauseAll, eventOptions);
      window.addEventListener('pageshow', resumeInlineVideos, eventOptions);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(root)) return;
        pauseAll();
        inlineObserver?.disconnect();
        modalObserver?.disconnect();
        window.clearTimeout(closeTimer);
        if (dialog && !dialog.hidden) document.body.classList.remove('product-reels-modal-open');
        eventController.abort();
      }, eventOptions);
  };
  const initWithin = (scope) => {
    if (scope.matches?.('[data-product-reels]')) init(scope);
    scope.querySelectorAll('[data-product-reels]').forEach(init);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();
